export const SPECIALIST_LOCATION_GATEWAY_ACTIVATION_SCHEMA_VERSION = 1 as const;
export const SPECIALIST_LOCATION_GATEWAY_ACTIVATION_REVISION =
  "specialist-location-gateway-activation-r1" as const;

export const SPECIALIST_LOCATION_GATEWAY_ACTIVATION_CONSUMERS = Object.freeze([
  "profile-entry",
  "location",
  "options",
  "services",
  "individual-draft",
  "group-step-one",
  "select-slot",
  "waitlist",
  "intake",
  "review",
  "payment",
  "submission",
  "confirmation",
  "package-review",
  "package-payment",
  "package-receipt",
  "terminal-origin-drain",
  "auth-teardown",
  "analytics",
  "live-provider",
  "web-responsive",
  "mobile-parity",
  "registry",
] as const);

export type SpecialistLocationGatewayActivationConsumer =
  (typeof SPECIALIST_LOCATION_GATEWAY_ACTIVATION_CONSUMERS)[number];

export interface SpecialistLocationGatewayActivationContractV1 {
  schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_ACTIVATION_SCHEMA_VERSION;
  kind: "specialist-location-gateway-activation";
  releaseStatus: "closed" | "open";
  consumers: Readonly<
    Record<SpecialistLocationGatewayActivationConsumer, "blocked" | "ready">
  >;
}

export type SpecialistLocationGatewayActivationResolution = Readonly<{
  schemaVersion: typeof SPECIALIST_LOCATION_GATEWAY_ACTIVATION_SCHEMA_VERSION;
  status: "active" | "closed";
  blockedConsumers: readonly SpecialistLocationGatewayActivationConsumer[];
}>;

const CURRENT_CONSUMERS = Object.freeze({
  "profile-entry": "ready",
  location: "ready",
  options: "ready",
  services: "blocked",
  "individual-draft": "ready",
  "group-step-one": "blocked",
  "select-slot": "blocked",
  waitlist: "blocked",
  intake: "blocked",
  review: "blocked",
  payment: "blocked",
  submission: "blocked",
  confirmation: "blocked",
  "package-review": "blocked",
  "package-payment": "blocked",
  "package-receipt": "blocked",
  "terminal-origin-drain": "blocked",
  "auth-teardown": "blocked",
  analytics: "blocked",
  "live-provider": "blocked",
  "web-responsive": "blocked",
  "mobile-parity": "blocked",
  registry: "blocked",
} as const satisfies SpecialistLocationGatewayActivationContractV1["consumers"]);

/**
 * Sole public activation ledger for Specialist Location Gateway. Individual
 * route modules may be exercised behind injected test runtimes, but no route,
 * provider, registry, or platform projection can activate itself independently.
 */
export const SPECIALIST_LOCATION_GATEWAY_ACTIVATION_CONTRACT = Object.freeze({
  schemaVersion: SPECIALIST_LOCATION_GATEWAY_ACTIVATION_SCHEMA_VERSION,
  kind: "specialist-location-gateway-activation",
  releaseStatus: "closed",
  consumers: CURRENT_CONSUMERS,
} as const satisfies SpecialistLocationGatewayActivationContractV1);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, expected: readonly string[]) {
  const actual = Object.keys(value).sort();
  const canonical = [...expected].sort();
  return (
    actual.length === canonical.length &&
    actual.every((key, index) => key === canonical[index])
  );
}

function createClosedSpecialistLocationGatewayActivationResolution(): SpecialistLocationGatewayActivationResolution {
  return Object.freeze({
    schemaVersion: SPECIALIST_LOCATION_GATEWAY_ACTIVATION_SCHEMA_VERSION,
    status: "closed",
    blockedConsumers: SPECIALIST_LOCATION_GATEWAY_ACTIVATION_CONSUMERS,
  });
}

export function resolveSpecialistLocationGatewayActivation(
  value: unknown = SPECIALIST_LOCATION_GATEWAY_ACTIVATION_CONTRACT,
): SpecialistLocationGatewayActivationResolution {
  try {
    if (
      !isRecord(value) ||
      !hasExactKeys(value, [
        "schemaVersion",
        "kind",
        "releaseStatus",
        "consumers",
      ]) ||
      value.schemaVersion !==
        SPECIALIST_LOCATION_GATEWAY_ACTIVATION_SCHEMA_VERSION ||
      value.kind !== "specialist-location-gateway-activation" ||
      (value.releaseStatus !== "closed" && value.releaseStatus !== "open") ||
      !isRecord(value.consumers) ||
      !hasExactKeys(
        value.consumers,
        SPECIALIST_LOCATION_GATEWAY_ACTIVATION_CONSUMERS,
      )
    ) {
      return createClosedSpecialistLocationGatewayActivationResolution();
    }

    const consumers = value.consumers as Record<string, unknown>;
    const blockedConsumers =
      SPECIALIST_LOCATION_GATEWAY_ACTIVATION_CONSUMERS.filter(
        (consumer) => consumers[consumer] !== "ready",
      );
    const active =
      value.releaseStatus === "open" && blockedConsumers.length === 0;
    return Object.freeze({
      schemaVersion: SPECIALIST_LOCATION_GATEWAY_ACTIVATION_SCHEMA_VERSION,
      status: active ? "active" : "closed",
      blockedConsumers: Object.freeze(blockedConsumers),
    });
  } catch {
    return createClosedSpecialistLocationGatewayActivationResolution();
  }
}

export const SPECIALIST_LOCATION_GATEWAY_ACTIVATION =
  resolveSpecialistLocationGatewayActivation();

export function canActivatePublicSpecialistLocationGateway() {
  return SPECIALIST_LOCATION_GATEWAY_ACTIVATION.status === "active";
}
